import { useCallback, useMemo } from 'react';

import {
  faCopy,
  faEye,
  faPencil,
  faTrashAlt
} from '@fortawesome/pro-light-svg-icons';
import { useTranslation } from 'react-i18next';

import { Box } from '@mui/material';
import { GridRenderCellParams } from '@mui/x-data-grid-pro';
import { GridEnrichedColDef } from '@mui/x-data-grid/models/colDef/gridColDef';

import { CustomDataGridActionMenuItem } from '../components/CustomDataGridActionMenuItem';
import { CustomDataGridAction } from '../components/customDataGridAction';

import { MoreActionsMenu } from '../../MoreActionsMenu';
import { CustomDataGridProps, DataGridAction } from '../DataGrid';

const lengthOfTheButtons = 45;

const createAction = (
  onClick: DataGridAction['onClick'],
  icon: DataGridAction['icon'],
  tooltipTitle: DataGridAction['tooltipTitle'],
  disabled?: DataGridAction['disabled']
): DataGridAction => ({
  onClick,
  icon,
  tooltipTitle,
  disabled
});

export const useGetCustomDataGridColumnsToRender = (
  onViewClick: CustomDataGridProps['onViewClick'],
  onCopyClick: CustomDataGridProps['onCopyClick'],
  onEditClick: CustomDataGridProps['onEditClick'],
  onDeleteClick: CustomDataGridProps['onDeleteClick'],
  handleDeleteClick: (id: string | number) => void,
  customActions: CustomDataGridProps['customActions'],
  columns: CustomDataGridProps['columns'],
  actionsThreshold: number,
  disableActions: CustomDataGridProps['disableActions'],
  canBeEditedProp: string | undefined,
  canBeDeletedProp: string | undefined
) => {
  const { t } = useTranslation('common');

  const renderActionButtons = useCallback(
    (params: GridRenderCellParams) => {
      const actions: DataGridAction[] = [];
      const canBeEdited = !canBeEditedProp || params.row[canBeEditedProp];
      const canBeDeleted = !canBeDeletedProp || params.row[canBeDeletedProp];

      if (onViewClick) {
        actions.push(createAction(onViewClick, faEye, t('details')));
      }
      if (onCopyClick) {
        actions.push(createAction(onCopyClick, faCopy, t('copy')));
      }
      if (onEditClick && canBeEdited) {
        actions.push(createAction(onEditClick, faPencil, t('edit'), true));
      }
      if (onDeleteClick && canBeDeleted) {
        actions.push(
          createAction(handleDeleteClick, faTrashAlt, t('remove'), true)
        );
      }
      if (typeof customActions === 'function') {
        actions.push(...(customActions(params.id, params.row) ?? []));
      } else {
        actions.push(...(customActions ?? []));
      }

      return (
        <Box display="flex" gap="8px">
          {actions.length <= actionsThreshold ? (
            actions.map((action, index) => (
              <CustomDataGridAction
                key={index}
                action={action}
                params={params}
              />
            ))
          ) : (
            <>
              {actions.slice(0, actionsThreshold - 1).map((action, index) => (
                <CustomDataGridAction
                  key={index}
                  action={action}
                  params={params}
                />
              ))}
              <MoreActionsMenu
                renderActions={(handleCloseMenu) =>
                  actions
                    .slice(actionsThreshold - 1)
                    .map((action, index) => (
                      <CustomDataGridActionMenuItem
                        key={index}
                        action={action}
                        params={params}
                        onCloseMenu={handleCloseMenu}
                      />
                    ))
                }
              />
            </>
          )}
        </Box>
      );
    },
    [
      onViewClick,
      onCopyClick,
      onEditClick,
      onDeleteClick,
      handleDeleteClick,
      customActions
    ]
  );

  return useMemo(
    () =>
      !disableActions
        ? [
            ...columns,
            ...(disableActions
              ? []
              : [
                  {
                    field: 'actions',
                    headerName: t('actions'),
                    width: lengthOfTheButtons * actionsThreshold,
                    renderCell: renderActionButtons,
                    minWidth: 75
                  } as GridEnrichedColDef
                ])
          ]
        : columns,
    [actionsThreshold, columns, renderActionButtons]
  );
};
